// menuTransformer.js - Utility to transform API response to menu structure

import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  FileOutlined,
  TeamOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  LinkOutlined,
  FileAddOutlined,
  UnorderedListOutlined,
  InboxOutlined,
  TableOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  TagOutlined,
  BarcodeOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ScanOutlined,
  BorderlessTableOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  FlagOutlined,
  IdcardOutlined,
  ApartmentOutlined,
  AppstoreAddOutlined,
  SafetyCertificateOutlined,
  FolderOutlined,
  UserAddOutlined,
  FileSearchOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  KeyOutlined,
  ClusterOutlined,
  ShareAltOutlined,
  FileDoneOutlined,
  LockOutlined,
  AuditOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  HistoryOutlined,
  PercentageOutlined,
  EditOutlined,
  FormOutlined,
  ApiOutlined,
  ContainerOutlined,
  FileProtectOutlined,
  CalculatorOutlined,
  PlusCircleOutlined,
  OrderedListOutlined,
  HourglassOutlined,
  WarningOutlined,
  SolutionOutlined,
  ContactsOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../utilities/apiMethod";

// ─── Parent menu icons keyed by MenuId ───────────────────────────────────────
const parentIconMap = {
  8:  <DashboardOutlined />,       // Dashboard (standalone)
  16: <AppstoreAddOutlined />,     // Master
  18: <ShopOutlined />,            // Vendor Master
  21: <FileProtectOutlined />,     // IRN Register
  25: <BarcodeOutlined />,         // Liquidation Barcode
  30: <WarningOutlined />,         // Near to Expiry
  43: <ApiOutlined />,             // SAP Integration Reports
  57: <ContainerOutlined />,       // GRN
  36: <SettingOutlined />,         // Admin Setting
  66: <BarChartOutlined />,        // Reports
};

// ─── Child / leaf menu icons keyed by FormId ─────────────────────────────────
const formIconMap = {
  // Dashboard
  1:  <DashboardOutlined />,       // Dashboard

  // Master
  2:  <GlobalOutlined />,          // Country
  3:  <FlagOutlined />,            // State
  4:  <EnvironmentOutlined />,     // City
  29: <CalculatorOutlined />,      // UOM
  5:  <SolutionOutlined />,        // Designation
  6:  <ApartmentOutlined />,       // Department
  8:  <TeamOutlined />,            // Employee

  // Vendor Master
  28: <ThunderboltOutlined />,     // Trigger Vendor
  35: <CheckCircleOutlined />,     // Vendor List For Approve
  40: <BarChartOutlined />,        // Vendor Report
  51: <AuditOutlined />,           // Vendor Audit Report
  45: <UploadOutlined />,          // Upload TOT Document
  34: <ContactsOutlined/>,

  // IRN Register
  15: <OrderedListOutlined />,     // IRN ALL PO
  16: <InboxOutlined />,           // Inward Register
  17: <TableOutlined />,           // IRN List Register

  // Liquidation Barcode
  18: <BarcodeOutlined />,         // Barcode Liquidation Master
  19: <HistoryOutlined />,         // Barcode Liquidation Aging Update
  20: <UnorderedListOutlined />,   // Barcode Liquidation List
  21: <FileTextOutlined />,        // Barcode Liquidation (Final Report)
  44: <ClockCircleOutlined />,     // Barcode Liquidation Aging All

  // Near to Expiry
  22: <UnorderedListOutlined />,   // Near to Expiry All List
  23: <ScanOutlined />,            // Near to Expiry Scan
  24: <PercentageOutlined />,      // Near to Expiry Discount
  25: <CheckCircleOutlined />,     // Near to Expiry List for Approve
  26: <CheckSquareOutlined />,     // Near to Expiry Approved List

  // SAP Integration Reports
  30: <ShopOutlined />,            // Store
  31: <TagsOutlined />,            // Category
  32: <DatabaseOutlined />,        // Item Master
  33: <ShoppingCartOutlined />,    // Purchase Order
  38: <WarningOutlined />,         // Near To Expiry (SAP)
  39: <FileSearchOutlined />,      // GRN List Report
  42: <HourglassOutlined />,       // Stock Aging

  // GRN Module
  46: <PlusCircleOutlined />,      // Create GRN
  43: <BarChartOutlined />,        // GRN Report
  48: <FileDoneOutlined />,        // GRN List

  // Reports
  52: <BarChartOutlined />,        // Division Wise PO GRN Summary Report
  53: <AreaChartOutlined />,       // Division Vendor Fill Rate

  // Admin Settings
  49: <FormOutlined />,            // Form Master
  9:  <FolderOutlined />,          // Menu Group
  10: <AppstoreOutlined />,        // Menu Setting
  11: <SafetyCertificateOutlined />, // Role Setting
  13: <KeyOutlined />,             // Role Permission
  12: <UserAddOutlined />,         // Register User
  14: <TeamOutlined />,            // Register User List
  36: <ClusterOutlined />,         // Approval Group
  37: <ShareAltOutlined />,        // Approval User Mapping
  41: <EditOutlined />,            // Update Legal Name
};

const getIconByMenuId  = (menuId)  => parentIconMap[menuId]  || <FileOutlined />;
const getIconByFormId  = (formId)  => formIconMap[formId]     || <FileOutlined />;

/**
 * Transform API response to menu items structure
 * @param {Array} apiData - Data array from API response
 * @returns {Array} - Transformed menu items for Ant Design Menu
 */
export const transformApiToMenuItems = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    console.error("Invalid API data received");
    return [];
  }

  const menuItems = [];

  apiData.forEach((item) => {
    // Skip items without access
    if (!item.AccessAllowed) return;

    // Handle parent menu with children
    if (item.IsChildMenu && item.Parent) {
      const parentKey = `menu_${item.Parent.MenuId}`;

      // Collect accessible children for this item
      const newChildren = [];
      if (item.Parent.Child && Array.isArray(item.Parent.Child)) {
        item.Parent.Child.forEach((child) => {
          if (child.AccessAllowed) {
            newChildren.push({
              key:    `form_${child.FormId}`,
              icon:   getIconByFormId(child.FormId),
              label:  child.Menu,
              formId: child.FormId,
            });
          }
        });
      }

      if (newChildren.length === 0) return;

      // Merge into existing parent if already added, otherwise push new entry
      const existingParent = menuItems.find((m) => m.key === parentKey);
      if (existingParent) {
        existingParent.children.push(...newChildren);
      } else {
        menuItems.push({
          key:      parentKey,
          icon:     getIconByMenuId(item.Parent.MenuId),
          label:    item.Parent.Menu,
          children: newChildren,
        });
      }
    }
    // Handle simple menu items without children (e.g. Dashboard)
    else if (item.Menu && !item.IsChildMenu) {
      menuItems.push({
        key:    `form_${item.FormId}`,
        icon:   getIconByFormId(item.FormId),
        label:  item.Menu,
        formId: item.FormId,
      });
    }
  });

  return menuItems;
};

/**
 * Fetch menu data from API
 * @returns {Promise<Array>} - Transformed menu items
 */
export const fetchDynamicMenuData = async () => {
  try {
    const result = await apiRequest("GET", "UserMenu/getusermenus");

    if (result && result.Data) {
      return transformApiToMenuItems(result.Data);
    } else {
      console.error("Invalid API response structure");
      return [];
    }
  } catch (error) {
    console.error("Error fetching menu data:", error);
    throw error;
  }
};